/**
 * RedeemConfirmationDialog Component
 * Shows confirmation dialog before redeeming a code
 * Displays preview of entitlements/badges that will be granted
 */

import { useEffect } from 'react';
import { useRedeemCode, useRedeemPreview } from '../../hooks/useRedeemApi';
import {
  EMAIL_NOT_VERIFIED_MESSAGES,
  isEmailNotVerifiedError,
} from '../../lib/emailNotVerified';
import { useBadgesList } from '../../hooks/useBadgeApi';
import { FormButton } from '../form/FormButton';
import { BadgeIcon } from '../badges/BadgeIcon';
import type { RedeemCodeResponse } from '../../lib/types/redeemGroup';

interface RedeemConfirmationDialogProps {
  code: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: RedeemCodeResponse) => void;
}

export function RedeemConfirmationDialog({
  code,
  isOpen,
  onClose,
  onSuccess,
}: RedeemConfirmationDialogProps) {
  const redeemMutation = useRedeemCode();

  // Fetch preview data for the code
  const previewQuery = useRedeemPreview(isOpen ? code : null);

  // Fetch badge catalog to get badge details
  const badgesQuery = useBadgesList();

  // Handle redemption
  const handleConfirm = async () => {
    try {
      const result = await redeemMutation.mutateAsync(code);
      onSuccess(result);
    } catch (_error) {
      // Error is handled by the mutation hook and displayed via toast
      // Keep dialog open so user can try again or close manually
    }
  };

  // Handle Escape key to close dialog (keyboard accessibility)
  useEffect(() => {
    if (!isOpen || redeemMutation.isPending) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, redeemMutation.isPending]);

  if (!isOpen) return null;

  // Get badge details for display - provide fallback for unknown badges
  const badgeDetails =
    previewQuery.data && badgesQuery.data
      ? previewQuery.data.badgeIds.map(badgeId => {
          const badge = badgesQuery.data!.badges.find(
            b => b.badgeId === badgeId
          );
          // Return actual badge or fallback object with badgeId
          return (
            badge || {
              badgeId,
              name: badgeId,
              icon_url: '',
              created_at: 0,
              // Deliberately not imageless. This branch is a badge the
              // catalogue could not explain, which is a failure to resolve —
              // so it keeps the pending/missing placeholder rather than
              // claiming the absence of an icon was intended.
              imageless: undefined,
            }
          );
        })
      : [];

  // Determine if we should disable the confirm button
  const isConfirmDisabled =
    redeemMutation.isPending || previewQuery.isLoading || previewQuery.isError;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={redeemMutation.isPending ? undefined : onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog-title"
          aria-describedby="dialog-description"
        >
          {/* Header */}
          <div className="mb-4">
            <h2
              id="dialog-title"
              className="text-xl font-semibold text-gray-900 dark:text-white"
            >
              Redeem Code
            </h2>
            <p
              id="dialog-description"
              className="text-sm text-gray-600 dark:text-gray-400 mt-1"
            >
              Confirm you want to redeem the following code
            </p>
          </div>

          {/* Code Display */}
          <div className="mb-6">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Code
              </p>
              <p className="text-lg font-mono font-bold text-gray-900 dark:text-white">
                {code}
              </p>
            </div>
          </div>

          {/* Preview Loading State */}
          {previewQuery.isLoading && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4" />
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/2" />
              </div>
            </div>
          )}

          {/* Preview Error */}
          {previewQuery.isError && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300">
                {previewQuery.error instanceof Error &&
                previewQuery.error.message.includes('HTTP 404')
                  ? 'Invalid code - code not found'
                  : previewQuery.error instanceof Error &&
                      previewQuery.error.message.includes('HTTP 409')
                    ? 'This code has already been redeemed'
                    : previewQuery.error instanceof Error &&
                        previewQuery.error.message.includes('HTTP 400')
                      ? 'This code is from a disabled group'
                      : 'Failed to load preview. Please try again.'}
              </p>
            </div>
          )}

          {/* Preview Content */}
          {previewQuery.data && !previewQuery.isLoading && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                This code will grant you:
              </p>

              {/* Badges Section */}
              {previewQuery.data.badgeIds.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Badges
                  </p>
                  <div className="space-y-2">
                    {badgeDetails.map(badge => (
                      <div
                        key={badge.badgeId}
                        className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                      >
                        <BadgeIcon
                          src={badge.icon_url}
                          alt={badge.name}
                          size="sm"
                          imageless={badge.imageless}
                        />
                        <span className="text-sm text-gray-900 dark:text-white">
                          {badge.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Entitlements Section */}
              {previewQuery.data.entitlementIds.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Entitlements
                  </p>
                  <div className="space-y-1">
                    {previewQuery.data.entitlementIds.map(entitlementId => (
                      <div
                        key={entitlementId}
                        className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                      >
                        <span className="text-sm font-mono text-gray-900 dark:text-white">
                          {entitlementId}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warning message */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-xs text-gray-700 dark:text-gray-300">
                  Once redeemed, you cannot undo this action.
                </p>
              </div>
            </div>
          )}

          {/* Redemption Error Message */}
          {redeemMutation.isError && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300">
                {/* Retrying cannot help an unverified address (#2037). */}
                {isEmailNotVerifiedError(redeemMutation.error)
                  ? EMAIL_NOT_VERIFIED_MESSAGES.redeem
                  : 'Failed to redeem code. Please try again or contact support if the problem persists.'}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={redeemMutation.isPending}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <FormButton
              type="button"
              onClick={handleConfirm}
              isLoading={redeemMutation.isPending}
              loadingText="Redeeming..."
              disabled={isConfirmDisabled}
              className="flex-1 btn-primary"
            >
              Confirm
            </FormButton>
          </div>
        </div>
      </div>
    </>
  );
}
